export const TERMINAL_ACTIVITY_STATES = ['completed', 'completed_with_errors', 'canceled', 'failed', 'ready'];
export const ATTENTION_ACTIVITY_STATES = ['stalled', 'failed', 'completed_with_errors'];

const defaultTranslate = (_key, fallback, values = {}) => Object.entries(values).reduce(
    (result, [name, value]) => result.replaceAll(`{{${name}}}`, value),
    fallback,
);

export const activityIsTerminal = activity =>
    Boolean(activity?.terminal || TERMINAL_ACTIVITY_STATES.includes(activity?.state));

export const mergeActivity = (current, incoming) => {
    if (!current) return incoming;
    const currentTime = Date.parse(current.updated_at);
    const incomingTime = Date.parse(incoming.updated_at);

    if (Number.isFinite(currentTime) && Number.isFinite(incomingTime) && incomingTime < currentTime) {
        return current;
    }
    if (
        activityIsTerminal(current) &&
        !activityIsTerminal(incoming) &&
        (!Number.isFinite(incomingTime) || !Number.isFinite(currentTime) || incomingTime <= currentTime)
    ) {
        return current;
    }
    return {...current, ...incoming};
};

export const reconcileActivityUpdates = (currentById = {}, activities = []) => {
    const workingById = {...currentById};
    const acceptedById = {};

    activities.filter(Boolean).forEach(activity => {
        const current = workingById[activity.id];
        const reconciled = mergeActivity(current, activity);
        if (reconciled === current) return;

        workingById[activity.id] = reconciled;
        acceptedById[activity.id] = reconciled;
    });

    return Object.values(acceptedById);
};

export const activityNeedsAttention = activity => ATTENTION_ACTIVITY_STATES.includes(activity?.state);

export const systemStatusIsDelayed = (status, now = Date.now()) => {
    if (status?.state !== 'delayed') return false;
    const observedAt = Date.parse(status.observed_at);
    return Number.isFinite(observedAt) && now - observedAt <= 6 * 60 * 1000;
};

export const activitySortPriority = activity => {
    if (activityNeedsAttention(activity)) return 0;
    if (!activityIsTerminal(activity)) return 1;
    return 2;
};

export const initialActivitiesState = {
    byId: {},
    dismissedIds: [],
    seenIds: [],
    loadStatus: 'idle',
    loadError: null,
    refreshStatus: 'idle',
    refreshError: null,
    systemStatus: {state: 'unavailable'},
};

export const backgroundActivitiesReducer = (state, action) => {
    switch (action.type) {
        case 'reset':
            return {
                ...initialActivitiesState,
                dismissedIds: action.dismissedIds || [],
                seenIds: action.seenIds || [],
            };
        case 'loadStarted':
            return {...state, loadStatus: 'loading', loadError: null};
        case 'loadSucceeded':
            return {
                ...state,
                loadStatus: 'loaded',
                loadError: null,
                systemStatus: action.systemStatus || state.systemStatus,
            };
        case 'loadFailed':
            return {...state, loadStatus: 'error', loadError: action.error};
        case 'refreshStatus':
            return {
                ...state,
                refreshStatus: action.status || 'idle',
                refreshError: action.error || null,
            };
        case 'merge': {
            const byId = {...state.byId};
            action.activities.filter(Boolean).forEach(activity => {
                byId[activity.id] = mergeActivity(byId[activity.id], activity);
            });
            const activeIds = new Set(action.activities
                .filter(activity => activity && !activityIsTerminal(byId[activity.id]))
                .map(activity => activity.id));
            return {
                ...state,
                byId,
                dismissedIds: state.dismissedIds.filter(id => !activeIds.has(id)),
                seenIds: state.seenIds.filter(id => !activeIds.has(id)),
            };
        }
        case 'replace': {
            const incomingById = {};
            action.activities.filter(Boolean).forEach(activity => {
                incomingById[activity.id] = mergeActivity(state.byId[activity.id], activity);
            });

            Object.entries(state.byId).forEach(([id, activity]) => {
                if (incomingById[id]) return;
                if (action.baselineById?.[id] !== activity) incomingById[id] = activity;
            });

            const activeIds = new Set(Object.values(incomingById)
                .filter(activity => !activityIsTerminal(activity))
                .map(activity => activity.id));
            return {
                ...state,
                byId: incomingById,
                dismissedIds: state.dismissedIds.filter(id => !activeIds.has(id)),
                seenIds: state.seenIds.filter(id => !activeIds.has(id)),
            };
        }
        case 'dismiss':
            return state.dismissedIds.includes(action.id)
                ? state
                : {...state, dismissedIds: [...state.dismissedIds, action.id]};
        case 'markSeen':
            return {
                ...state,
                seenIds: Array.from(new Set([...state.seenIds, ...(action.ids || [])])),
            };
        case 'clearTerminal': {
            const terminalIds = Object.values(state.byId).filter(activityIsTerminal).map(activity => activity.id);
            return {...state, dismissedIds: Array.from(new Set([...state.dismissedIds, ...terminalIds]))};
        }
        default:
            return state;
    }
};

export const visibleActivities = state => Object.values(state.byId)
    .filter(activity => !state.dismissedIds.includes(activity.id))
    .sort((a, b) => {
        const priority = activitySortPriority(a) - activitySortPriority(b);
        if (priority) return priority;
        return Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at);
    });

export const unnotifiedTerminalActivities = (activities, notifiedIds) => {
    const notified = new Set(notifiedIds);
    return activities.filter(activity => activityIsTerminal(activity) && !notified.has(activity.id));
};

export const activityNotificationTitle = (activity, translate = (_key, fallback) => fallback) => {
    if (activity.state === 'completed') return translate('job-completed', 'Job completed');
    if (activity.state === 'completed_with_errors') return translate('job-completed-with-errors', 'Completed with errors');
    if (activity.state === 'failed') return translate('job-failed', 'Job failed');
    if (activity.state === 'ready') return translate('import-ready', 'Ready to import');
    return translate('job-canceled', 'Job canceled');
};

export const activityNotificationMethod = activity => {
    if (activity.state === 'failed') return 'error';
    if (['canceled', 'completed_with_errors'].includes(activity.state)) return 'warning';
    return 'success';
};

export const activityPhaseLabel = (activity, translate = (_key, fallback) => fallback) => {
    const phase = activity?.phase || activity?.state;
    const labels = {
        uploading: ['activity-phase-uploading', 'Uploading'],
        waiting_to_upload: ['activity-phase-waiting-to-upload', 'Waiting to upload'],
        waiting: ['activity-phase-waiting', 'Waiting to process'],
        queued: ['activity-phase-waiting', 'Waiting to process'],
        processing: ['activity-phase-processing', 'Processing files'],
        indexing: ['activity-phase-updating-library', 'Updating library'],
        updating_library: ['activity-phase-updating-library', 'Updating library'],
        paused: ['activity-phase-paused', 'Paused'],
        stalled: ['activity-phase-needs-attention', 'Needs attention'],
        needs_attention: ['activity-phase-needs-attention', 'Needs attention'],
        ready: ['activity-phase-ready', 'Ready'],
        completed: ['activity-phase-completed', 'Completed'],
        completed_with_errors: ['activity-phase-completed-with-errors', 'Completed with errors'],
        failed: ['activity-phase-failed', 'Failed'],
        canceled: ['activity-phase-canceled', 'Canceled'],
    };
    const [key, fallback] = labels[phase] || ['activity-phase-background-work', 'Background work'];
    return translate(key, fallback);
};

export const activityProgressText = (activity, translate = defaultTranslate) => {
    const counts = activity?.counts;
    if (!counts?.total) return null;
    const processed = Number(counts.completed ?? counts.processed ?? 0);
    const failed = Number(counts.failed ?? 0);
    const itemNoun = activity?.kind === 'bulk_job' ? 'items' : 'files';
    if (failed > 0) {
        return translate(
            `activity-${itemNoun}-progress-with-failures`,
            `{{processed}} processed, {{failed}} failed, {{total}} ${itemNoun} total`,
            {processed, failed, total: counts.total},
        );
    }
    return translate(
        `activity-${itemNoun}-progress`,
        `{{processed}} of {{total}} ${itemNoun} processed`,
        {processed, total: counts.total},
    );
};

export const activityDelayMessage = (
    activity,
    now = Date.now(),
    translate = (_key, fallback) => fallback,
) => {
    if (activity?.state === 'stalled') {
        return activity.action_path
            ? translate('activity-stalled-next-step', 'Open the upload to resume or retry.')
            : translate('activity-stalled-no-action', 'This activity needs attention.');
    }
    if (activity?.state === 'paused') {
        return activity.action_path
            ? translate('activity-paused-next-step', 'Open the upload when you are ready to resume.')
            : translate('activity-paused-no-action', 'This activity is paused.');
    }
    if (activityIsTerminal(activity)) return null;

    const updatedAt = Date.parse(activity?.updated_at || activity?.created_at);
    if (!Number.isFinite(updatedAt) || now - updatedAt < 10 * 60 * 1000) return null;
    return activity.action_path
        ? translate('activity-waiting-longer', 'Waiting longer than usual. Open the activity for details.')
        : translate('activity-waiting-longer-no-action', 'Waiting longer than usual. The status will update here.');
};

export const activityFromCableEvent = data => {
    if (data?.activity) return data.activity;
    if (data?.type !== 'bulkJobProgress' || !data.guid) return null;

    const failed = Boolean(data.error) || data.aasm_state === 'failed';
    const canceled = data.canceled || data.aasm_state === 'canceled';
    const completed = data.done && !data.indexing_pending;
    const state = failed ? 'failed' : canceled ? 'canceled' : completed ? 'completed' : data.indexing_pending ? 'indexing' : 'processing';

    return {
        id: `bulk_job:${data.guid}`,
        kind: 'bulk_job',
        guid: data.guid,
        summary: data.action || 'Background job',
        state,
        terminal: TERMINAL_ACTIVITY_STATES.includes(state),
        progress: data.progress || 0,
        total: data.total_assets || 0,
        error: data.error,
        updated_at: data.updated_at || new Date().toISOString(),
    };
};
